/**
 * Menu Route Guard
 * Componente guard che protegge le routes in base alla configurazione menu
 */
import { Navigate, useLocation } from 'react-router';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { storage } from '@/lib/local-storage';

interface MenuRouteGuardProps {
    children: React.ReactNode;
}

/**
 * Route Guard che verifica se l'utente ha accesso al route corrente
 * in base alla configurazione menu della sua company
 */
export function MenuRouteGuard({ children }: MenuRouteGuardProps) {
    const location = useLocation();
    const { isRouteAccessible, isLoading, isAdmin } = useMenuConfiguration();
    const userType = storage.getItem('userType')?.toLowerCase();
    
    // Admin bypassa sempre il guard
    if (isAdmin) {
        return <>{children}</>;
    }
    
    // Mentre sta caricando, mostra i children (evita flash)
    // La config viene cachata quindi dopo la prima volta sarà istantanea
    if (isLoading) {
        return <>{children}</>;
    }
    
    // Verifica se il route è accessibile
    const accessible = isRouteAccessible(location.pathname);
    
    if (!accessible) {
        // Route non accessibile, redirect alla dashboard del tipo utente
        const dashboardPath = userType ? `/${userType}/dashboard` : '/';
        
        console.warn(`Access denied to ${location.pathname}, redirecting to ${dashboardPath}`);
        
        return <Navigate to={dashboardPath} replace />;
    }
    
    // Route accessibile, mostra i children
    return <>{children}</>;
}

/**
 * Higher Order Component per wrappare facilmente le routes con il guard
 */
export function withMenuRouteGuard<P extends object>(
    Component: React.ComponentType<P>
): React.FC<P> {
    return (props: P) => (
        <MenuRouteGuard>
            <Component {...props} />
        </MenuRouteGuard>
    );
}

